import React, { useEffect, useRef, useState } from 'react';
import { useInView, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon, BatteryChargingIcon, RefreshCwIcon } from 'lucide-react';
import { rotationSteps } from '../../data/mountSystem';
import { useLanguage } from '../../contexts/LanguageContext';
import { bundleName, formatMoney, getBundle, priceBundle } from '../../utils/pricing';
import { useShopNavigation } from '../../hooks/useShopNavigation';
import { track } from '../../utils/analytics';
import { buttonClasses } from '../../utils/buttonStyles';
import { RotationPoolScene, type LightSet } from './RotationPoolScene';

/** 8-Light Signature System weekly rotation: 4 installed + 4 charged, swapped onto the same bases. */
export function RotationSystem({
  showOffer = true
}: {
  showOffer?: boolean;
}) {
  const {
    lang
  } = useLanguage();
  const reduce = useReducedMotion();
  const shop = useShopNavigation();
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    amount: 0.4
  });
  const [installed, setInstalled] = useState<LightSet>('A');
  const [auto, setAuto] = useState(true);
  useEffect(() => {
    if (!auto || reduce || !inView) return;
    const id = window.setInterval(() => setInstalled(s => s === 'A' ? 'B' : 'A'), 3600);
    return () => window.clearInterval(id);
  }, [auto, reduce, inView]);
  const charging: LightSet = installed === 'A' ? 'B' : 'A';
  const bundle = getBundle('sys8');
  const p = priceBundle(bundle);
  const m = (n: number) => formatMoney(n, lang);
  const swap = () => {
    setAuto(false);
    setInstalled(charging);
  };
  return <div ref={ref} className="grid gap-10 lg:grid-cols-[1.15fr_1fr] lg:gap-16" data-id="element-608" data-mp="3i57zh924md6k">
      <div data-id="element-609" data-mp="3d7q0aadwvcbr">
        <figure className="mf-hud border border-marine/25 bg-[#06121F] p-3 md:p-6" data-id="element-610" data-mp="3d654khfv1ncb">
          <RotationPoolScene installed={installed} data-id="element-611" data-mp="ehpvcwjq2fyy" />
        </figure>

        <div className="mt-4 flex flex-col gap-3 border border-white/10 bg-[#06121F] p-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-612" data-mp="ej0togueevio">
          <div className="flex items-center gap-3" data-id="element-613" data-mp="23y1x78yqe3bl">
            <BatteryChargingIcon className="h-5 w-5 shrink-0 text-ice" aria-hidden data-id="element-614" data-mp="240o1mzqbs2pl" />
            <p className="font-display text-sm font-semibold uppercase tracking-[0.1em] text-white" data-id="element-615" data-mp="28wlkpdsz3n62">
              Set {installed} lighting <span className="text-titanium/60" data-id="element-616" data-mp="1z7lg9evpbzkh">·</span>{' '}
              <span className="text-ice" data-id="element-617" data-mp="1z60ilge3c0ie">Set {charging} charging</span>
            </p>
          </div>
          <button type="button" onClick={swap} className={`${buttonClasses('secondary')} h-11 shrink-0`} data-id="element-618" data-mp="1l6iruvh8id6e">
            <RefreshCwIcon className="h-4 w-4" aria-hidden data-id="element-619" data-mp="1ba5xrwhs1xb4" /> Swap the sets
          </button>
        </div>
        <p className="sr-only" aria-live="polite" data-id="element-620" data-mp="1g7xs0x5w9vih">
          Set {installed} installed. Set {charging} charging.
        </p>
      </div>

      <div className="flex flex-col" data-id="element-621" data-mp="35ogoybluh3k2">
        <ol className="space-y-5" data-id="element-622" data-mp="3ami80wj9t2vj">
          {rotationSteps.map((s, i) => <li key={i} className="grid grid-cols-[2rem_1fr] gap-3" data-id="element-623" data-mp="3ake6gl8ugfyf" data-mpi={i}>
              <span className="font-display text-lg font-semibold text-marine" data-id="element-624" data-mp="yzwtf3jppqm7">{i + 1}</span>
              <p className="text-titanium" data-id="element-625" data-mp="u1mvwmaimdxf">{s}</p>
            </li>)}
        </ol>
        <p className="mt-8 font-display text-2xl font-semibold uppercase tracking-[0.04em] text-ice md:text-3xl" data-id="element-626" data-mp="18q9lrhxrqo2w">
          No remounting. No guesswork. Just light.
        </p>

        {showOffer && <div className="mf-hud mt-8 border border-marine bg-[#0D2440] p-6" data-id="element-627" data-mp="1holblhqt9hua">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-marine" data-id="element-628" data-mp="1crbgpmknkdz2">Most popular</p>
          <p className="mt-1 font-display text-2xl font-semibold uppercase tracking-[0.03em] text-white" data-id="element-629" data-mp="1roc6hkja9yuw">{bundleName(bundle, lang)}</p>
          <div className="mt-4 flex flex-wrap items-baseline gap-x-4 gap-y-1" data-id="element-630" data-mp="1mpivbyrgyjxw">
            <span className="font-display text-5xl font-semibold text-white" data-id="element-631" data-mp="3tggxmtsyks4k">{m(p.total)}</span>
            {p.savings > 0 && <span className="text-sm font-semibold uppercase tracking-[0.08em] text-ice" data-id="element-632" data-mp="3ohx8ylmzd28i">Save {m(p.savings)}</span>}
          </div>
          <p className="mt-1 text-sm text-titanium" data-id="element-633" data-mp="2flrxy4tfcq0z">
            {m(p.perUnit)} per light · 8 M1 PRO lights for 4 permanent positions
          </p>
          <button type="button" onClick={() => {
          track('mount_rotation_cta', {
            bundleId: 'sys8',
            value: p.total
          });
          shop('sys8');
        }} className={`${buttonClasses('primary', 'lg')} mt-6 w-full`} data-id="element-634" data-mp="2pimzkwkzv6jh">
            Choose the 8-Light System <ArrowRightIcon className="h-4 w-4" aria-hidden data-id="element-635" data-mp="21ssnih8i1m0a" />
          </button>
        </div>}
      </div>
    </div>;
}